import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { NotifyService } from 'app/core/services/notify/notify.service';
import { AuthenticatedCustomerService } from 'app/core/services/prothesis-customer-api/authenticated/authenticated-customer.service';
import { AutoCompleteResultProjection } from 'app/shared/prothesis-api-projections/auto-complete-result.projection';
import { TenantFilter } from 'app/shared/prothesis-base-api-filters/tenant-filter';
import { InfoCustomerViewModel } from 'app/shared/prothesis-customer-api-view-models/info-customer.view-model';
import { PickerAllowedCustomerViewModel } from 'app/shared/prothesis-customer-api-view-models/picker-allowed-customer.view-model';
import { PickerState } from 'app/shared/states/picker-state';
import { Observable } from 'rxjs';
import { ComboModel } from '../../combo/combo-model';
import { ElementModel } from '../picker/element.model';
import { PickerComponent } from '../picker/picker.component';
import { CustomerTenantModelHelper } from './model-helpers/customer-tenant-model-helper';
import { CustomerTenantModel } from './models/customer-tenant-model';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-customer-tenant-picker',
  templateUrl: './customer-tenant-picker.component.html',
  styleUrls: ['./customer-tenant-picker.component.scss'],
  imports: [PickerComponent],
})
export class CustomerTenantPickerComponent implements OnChanges {
  @ViewChild('_pickerComponent', { static: true }) public _pickerComponent: PickerComponent;
  @Output() public customerTenantAddedEvent = new EventEmitter<CustomerTenantModel>();
  @Output() public pickerDialogAfterClosedEvent = new EventEmitter<void>();
  @Input() public textFilterDefaultValue: string;
  public autoCompleteResultList: AutoCompleteResultProjection[];
  public elements: ElementModel[] = new Array<ElementModel>();
  public existenceFlag = false;
  public families: ComboModel[] = new Array<ComboModel>();
  public loadedFlag: boolean;
  public pickerState: PickerState = new PickerState();
  public showPickerFlag = true;
  public totalCount = 0;
  private _authenticatedCustomerService = inject(AuthenticatedCustomerService);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _notifyService = inject(NotifyService);
  private _viewModels: InfoCustomerViewModel[] = new Array<InfoCustomerViewModel>();

  public ngOnChanges(): void {
    if (this.textFilterDefaultValue) {
      this.textFilterDefaultValue = this.textFilterDefaultValue.trim();
    }
  }

  public pickerDialogAfterClosedEventHandler(): void {
    this.pickerDialogAfterClosedEvent.emit();
  }

  public selectElement(element: ElementModel): void {
    const viewModel = this._viewModels.find((c) => c.uuid === element.key);
    if (viewModel) {
      const models = CustomerTenantModelHelper.fromInfoCustomerViewModel([viewModel]);
      this.showPickerFlag = false;
      this.customerTenantAddedEvent.emit(models[0]);
    }
  }

  public setFocus(): void {
    this._pickerComponent?.setFocus();
  }

  public updateState(pickerState: PickerState): void {
    this.pickerState = pickerState;
    this._refreshList();
  }

  public uuidSelectedHandler(uuid: string): void {
    const filter = new TenantFilter();
    filter.uuids = [uuid];
    this._authenticatedCustomerService.getAll(filter, 'code_asc', 0, 1).subscribe((indexAllowedCustomerViewModel) => {
      if (indexAllowedCustomerViewModel.resultList && indexAllowedCustomerViewModel.resultList.values) {
        if (indexAllowedCustomerViewModel.resultList.values.length === 1) {
          const models = CustomerTenantModelHelper.fromReadAllowedCustomerViewModel(indexAllowedCustomerViewModel.resultList.values);
          this.customerTenantAddedEvent.emit(models[0]);
        } else {
          this._notifyService.displayErrorMessagesNow(`Laboratoire inexistant.`);
        }
      }
    });
  }

  private _load(): Observable<PickerAllowedCustomerViewModel> {
    const filter = this._setPickerFilter();
    const sort = 'label_asc';
    return this._authenticatedCustomerService.initializePicker(filter, sort, this.pickerState.pageIndex, this.pickerState.pageSize);
  }

  private _refreshList(): void {
    this.loadedFlag = false;
    this._load().subscribe((result: PickerAllowedCustomerViewModel) => {
      this.existenceFlag = result.existenceFlag;

      // Handle resultList
      if (result.resultList) {
        this.totalCount = result.resultList.aggregate?.count;

        // View model to model conversion
        this.elements = [];
        this._changeDetectorRef.markForCheck();
        this._viewModels = result.resultList.values;
        const models = CustomerTenantModelHelper.fromInfoCustomerViewModel(this._viewModels);
        models.forEach((model) => {
          const element = new ElementModel(null, model.name, model.uuid, model.laboratoryName, null);

          if (model.customerGroupName) {
            element.hat = `${model.customerGroupName}`;
            element.hatStyle = 'primary-border';
            element.hatWidth = 'fixed';
            element.highlightHatFlag = true;
          }

          if (model.customerGroupFlag) {
            element.hat = 'Groupe de facturation';
            element.hatStyle = 'primary';
            element.hatWidth = 'fixed';
            element.highlightHatFlag = false;
          }

          this.elements.push(element);
        });
      }

      this.loadedFlag = true;
      this._notifyService.displayMessages();
    });
  }

  private _setPickerFilter(): TenantFilter {
    const filter = new TenantFilter();
    filter.textFilter = this.pickerState.textFilter;
    filter.textFilterFields = ['customerGroupName', 'laboratoryName', 'name'];
    return filter;
  }
}
